"use client";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/app/components/ui/select";
import type { PublishedMakeT } from "@/app/services/server/frontend/get-published-makes";
import type { PublishedOfferTypeT } from "@/app/services/server/frontend/get-published-offer-types";
import type { PublishedCampaignTypeT } from "@/app/services/server/frontend/get-published-campaign-types";

interface CardFiltersProps {
  selectedBrand: string;
  selectedType: string;
  onBrandChange: (brand: string) => void;
  onTypeChange: (type: string) => void;
  makes: PublishedMakeT[];
  offerTypes: PublishedOfferTypeT[];
  campaignTypes: PublishedCampaignTypeT[];
}

/**
 * Card filter component for filtering offers and campaigns by brand and type.
 * Loads filter options dynamically from the database.
 */
export default function CardFilters({
  selectedBrand,
  selectedType,
  onBrandChange,
  onTypeChange,
  makes,
  offerTypes,
  campaignTypes,
}: CardFiltersProps) {
  // Combine offer types and campaign types, removing duplicates by label
  const allTypes = [
    ...offerTypes.map((type) => ({ label: type.label, key: type.key })),
    ...campaignTypes.map((type) => ({ label: type.label, key: type.key })),
  ];

  // Remove duplicates based on label (case-insensitive)
  const uniqueTypes = allTypes.filter(
    (type, index, self) =>
      index ===
      self.findIndex((t) => t.label.toLowerCase() === type.label.toLowerCase())
  );

  return (
    <div className="mt-6 flex gap-4">
      <Select value={selectedBrand} onValueChange={onBrandChange}>
        <SelectTrigger className="bg-background w-full truncate py-6">
          <SelectValue placeholder="Marke wählen" />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectLabel>Marke wählen</SelectLabel>
            <SelectItem value="all">Alle Marken</SelectItem>
            {makes.map((make) => (
              <SelectItem key={make.id} value={make.name}>
                {make.name}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
      <Select value={selectedType} onValueChange={onTypeChange}>
        <SelectTrigger className="bg-background w-full py-6">
          <SelectValue placeholder="Typ wählen" />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectLabel>Typ wählen</SelectLabel>
            <SelectItem value="all">Privat & Gewerbe</SelectItem>
            {uniqueTypes.map((type) => (
              <SelectItem key={type.key} value={type.label}>
                {type.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  );
}
